import { Box, Text, VStack } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';

import { ConversationMessage } from './ConversationMessage';
import styles from './ConversationView.module.css';

interface ConversationViewProps {
  promptHistory: string[];
  responseSummaries: string[];
  thinking: string[];
  isGenerating: boolean;
  /** Dashboard LLMTabContent or report ReportLLMTabContent */
  configSnapshots: unknown[];
  restoreConfig: (index: number) => void;
}

export function ConversationView({
  promptHistory,
  responseSummaries,
  thinking,
  isGenerating,
  configSnapshots,
  restoreConfig,
}: ConversationViewProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [promptHistory.length, responseSummaries.length, thinking.length]);

  const messages = (() => {
    type Message = {
      role: 'user' | 'assistant';
      content: string;
      thinking?: string;
      responseIndex?: number;
    };
    return promptHistory.flatMap((prompt, i) => {
      const userMsg: Message = { role: 'user', content: prompt || '' };
      const hasResponse = i < responseSummaries.length;
      const isLastAndGenerating =
        isGenerating &&
        i === promptHistory.length - 1 &&
        i >= responseSummaries.length;
      if (hasResponse) {
        return [
          userMsg,
          {
            role: 'assistant' as const,
            content: responseSummaries[i] || '',
            responseIndex: i,
          },
        ];
      }
      if (isLastAndGenerating) {
        return [
          userMsg,
          {
            role: 'assistant' as const,
            content: '',
            thinking: thinking.join(''),
          },
        ];
      }
      return [userMsg];
    });
  })();

  return (
    <Box
      ref={scrollRef}
      flex={1}
      overflowY="auto"
      className={styles.conversationContainer}
    >
      <VStack align="stretch" spacing={0} p={2}>
        {messages.length === 0 && !isGenerating && (
          <Box textAlign="center" py={8} color="gray.500">
            <Text fontSize="sm">Start a conversation to see messages here</Text>
          </Box>
        )}
        {messages.map((message, index) => {
          const isAssistantMessage = message.role === 'assistant';
          const responseIndex = message.responseIndex;
          const hasConfigSnapshot =
            isAssistantMessage &&
            responseIndex !== undefined &&
            configSnapshots[responseIndex] !== undefined;
          const isCurrentlyGenerating =
            isGenerating &&
            index === messages.length - 1 &&
            message.role === 'assistant';

          return (
            <ConversationMessage
              key={index}
              messageRole={message.role}
              content={message.content}
              thinking={message.thinking}
              isGenerating={isCurrentlyGenerating}
              showRestore={hasConfigSnapshot && !isCurrentlyGenerating}
              onRestore={
                hasConfigSnapshot && responseIndex !== undefined
                  ? () => restoreConfig(responseIndex)
                  : undefined
              }
            />
          );
        })}
        {isGenerating && messages.length === 0 && (
          <ConversationMessage
            messageRole="assistant"
            content=""
            thinking={thinking.join('')}
            isGenerating
          />
        )}
      </VStack>
    </Box>
  );
}
